/** @jsxImportSource @emotion/react */
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { Link } from 'react-router-dom'
import IconButton from '@mui/material/IconButton'
import { useCallback } from 'react'
import Switch from '@mui/material/Switch'
import { Button } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { last } from 'lodash'
import {
  findAllAchievements,
  findAllAchievementsVariables,
  findAllAchievements_findAllAchievements,
} from '__generatedTypes__/findAllAchievements'
import { BULK_UPDATE_ACHIEVEMENTS, FIND_ALL_ACHIEVEMENTS, UPDATE_ACHIEVEMENT } from 'queries/achievements'
import Loader from 'components/Loader'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import Table from 'components/Table'

import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { updateAchievement, updateAchievementVariables } from '__generatedTypes__/updateAchievement'
import { bulkUpdateAchievements, bulkUpdateAchievementsVariables } from '__generatedTypes__/bulkUpdateAchievements'
import { OrderDirection } from '__generatedTypes__/globalTypes'
import { toast } from 'utils/toast'
import { PlatformSlug } from 'constants/consts'
import { ORDER_PARAM } from '../consts'
import { MAP_ENUM_TO_TITLE, getAwardTitle } from './consts'
import * as classes from './styles'
import { READ_ONLY_FOR_PLATFORMS } from './Form'

type Props = {
  platform: Platform
}

export const AchievementsList = withPlatformCheck(({ platform }: Props) => {
  const apolloClient = useApolloClient()
  const { navigate, getLink } = useLink()
  const isReadOnly = READ_ONLY_FOR_PLATFORMS.includes(platform.slug as PlatformSlug)

  const findAllAchievementsVariables = {
    query: {
      where: {
        platform: {
          id: platform.id,
        },
      },
      order: {
        order: OrderDirection.ASC,
      },
    },
  }

  const { data, loading } = useQuery<findAllAchievements, findAllAchievementsVariables>(FIND_ALL_ACHIEVEMENTS, {
    fetchPolicy: 'network-only',
    variables: findAllAchievementsVariables,
  })

  const [updateAchievement] = useMutation<updateAchievement, updateAchievementVariables>(UPDATE_ACHIEVEMENT)
  const [bulkUpdateAchievements] = useMutation<bulkUpdateAchievements, bulkUpdateAchievementsVariables>(
    BULK_UPDATE_ACHIEVEMENTS
  )

  const handleSort = useCallback(async (achievementsUpd: findAllAchievements_findAllAchievements[]) => {
    try {
      apolloClient.writeQuery({
        query: FIND_ALL_ACHIEVEMENTS,
        variables: findAllAchievementsVariables,
        data: {
          findAllAchievements: achievementsUpd,
        },
      })

      await bulkUpdateAchievements({
        variables: {
          bulkUpdateAchievementsInput: achievementsUpd.map(({ id }, order) => ({ id, order })),
        },
      })

      toast.success('Achievements order updated successfully')
    } catch (error) {
      toast.error('Failed to update achievements order')
    }
  }, [])

  const handleIsLiveChange = async (id: string, isLive: boolean) => {
    try {
      apolloClient.cache.modify({
        id: apolloClient.cache.identify({
          __typename: 'AchievementType',
          id,
        }),
        fields: {
          isLive: () => isLive,
        },
      })

      await updateAchievement({
        variables: {
          updateAchievementInput: {
            id,
            isLive,
          },
        },
      })

      toast.success('Achievement updated successfully')
    } catch (error) {
      toast.error('Failed to update achievement')
    }
  }

  if (loading) {
    return <Loader />
  }

  const achievements = data?.findAllAchievements ?? []

  const lastAchievementOrder = last(achievements)?.order ?? 0

  return (
    <div>
      <Table
        columns={[
          {
            title: 'Achievement Type',
            renderCell: (achievement) => MAP_ENUM_TO_TITLE[achievement.type],
            flex: 2,
          },
          {
            title: 'Reward',
            renderCell: (achievement) => (
              <div>{getAwardTitle(achievement.awardType, achievement.awardTypeValue) || '–'}</div>
            ),
            flex: 2,
          },
          {
            title: 'Description',
            renderCell: (achievement) => achievement.description,
            flex: 2,
          },
          {
            title: 'Live',
            renderCell: (achievement) => (
              <Switch
                checked={achievement.isLive}
                onClick={(e) => e.stopPropagation()}
                onChange={(_e, isChecked) => handleIsLiveChange(achievement.id, isChecked)}
                disabled={isReadOnly}
              />
            ),
            flex: '0 1 60px',
          },
          {
            renderCell: (achievement) => (
              <Link to={getLink(cmsRoutes.ACHIEVEMENTS_EDIT, { achievementId: achievement.id })}>
                <IconButton size="small">
                  <EditIcon />
                </IconButton>
              </Link>
            ),
            flex: '0 1 30px',
          },
        ]}
        items={achievements}
        onSort={handleSort}
        isSortable
      />
      <Button
        variant="outlined"
        size="large"
        color="blue"
        fullWidth
        css={classes.addButton}
        onClick={() =>
          navigate(cmsRoutes.ACHIEVEMENTS_CREATE, undefined, {
            searchParams: new URLSearchParams({
              [ORDER_PARAM]: String(lastAchievementOrder),
            }),
          })
        }
        disabled={isReadOnly}
      >
        Add Achievement <AddRoundedIcon />
      </Button>
    </div>
  )
})
